export const DEBUG_LOG_QUERY_PARAM = 'debugLog';
export const DEBUG_LOG_STORAGE_KEY = 'konai_debug_log_v1';
export const DEBUG_LOG_CATEGORIES = ['scene', 'scenario', 'audio', 'save', 'input'] as const;

export type DebugLogCategory = (typeof DEBUG_LOG_CATEGORIES)[number];
export type DebugLogCategorySelection = 'all' | readonly DebugLogCategory[];

export interface DebugLogConfig {
  enabled: boolean;
  categories: DebugLogCategorySelection;
  unknownCategories: string[];
  source: 'query' | 'storage' | 'default';
  rawValue: string | null;
}

export interface DebugLogEntry {
  timestamp: string;
  category: DebugLogCategory;
  message: string;
  details?: unknown;
}

export type DebugLogSink = (entry: DebugLogEntry) => void;

interface DebugLoggerOptions extends DebugLogConfig {
  sink?: DebugLogSink;
  now?: () => string;
}

const ENABLE_ALL_VALUES = new Set(['1', 'true', 'all', '*', 'on', 'yes']);
const DISABLE_VALUES = new Set(['0', 'false', 'off', 'no', 'none', '']);
const CATEGORY_SET: ReadonlySet<string> = new Set(DEBUG_LOG_CATEGORIES);

export class DebugLogger {
  private readonly categorySet: ReadonlySet<DebugLogCategory> | null;
  private readonly sink: DebugLogSink;
  private readonly now: () => string;
  private readonly enabled: boolean;

  public constructor(options: DebugLoggerOptions) {
    this.enabled = options.enabled;
    this.categorySet = options.categories === 'all' ? null : new Set(options.categories);
    this.sink = options.sink ?? writeConsoleDebug;
    this.now = options.now ?? (() => new Date().toISOString());
  }

  public isEnabled(category?: DebugLogCategory): boolean {
    if (!this.enabled) {
      return false;
    }

    return category === undefined || this.categorySet === null || this.categorySet.has(category);
  }

  public log(category: DebugLogCategory, message: string, details?: unknown): void {
    if (!this.isEnabled(category)) {
      return;
    }

    this.sink({
      timestamp: this.now(),
      category,
      message,
      details
    });
  }

  public getConfig(): Pick<DebugLogConfig, 'enabled' | 'categories'> {
    return {
      enabled: this.enabled,
      categories: this.categorySet === null ? 'all' : [...this.categorySet].sort()
    };
  }
}

export function resolveDebugLogConfig(
  rawValue: string | null | undefined,
  options: { dev?: boolean; source?: DebugLogConfig['source'] } = {}
): DebugLogConfig {
  const source = options.source ?? 'default';
  const normalizedRawValue = rawValue ?? null;
  if (!options.dev) {
    return {
      enabled: false,
      categories: [],
      unknownCategories: [],
      source,
      rawValue: normalizedRawValue
    };
  }

  const value = normalizedRawValue?.trim() ?? '';
  const normalizedValue = value.toLowerCase();
  if (DISABLE_VALUES.has(normalizedValue)) {
    return {
      enabled: false,
      categories: [],
      unknownCategories: [],
      source,
      rawValue: normalizedRawValue
    };
  }

  if (ENABLE_ALL_VALUES.has(normalizedValue)) {
    return {
      enabled: true,
      categories: 'all',
      unknownCategories: [],
      source,
      rawValue: normalizedRawValue
    };
  }

  const categories: DebugLogCategory[] = [];
  const unknownCategories: string[] = [];
  for (const token of normalizedValue.split(',')) {
    const category = token.trim();
    if (!category) {
      continue;
    }

    if (isDebugLogCategory(category)) {
      if (!categories.includes(category)) {
        categories.push(category);
      }
      continue;
    }

    unknownCategories.push(category);
  }

  return {
    enabled: categories.length > 0,
    categories,
    unknownCategories,
    source,
    rawValue: normalizedRawValue
  };
}

export function createDebugLogger(): DebugLogger {
  const config = resolveBrowserDebugLogConfig(import.meta.env.DEV);
  return new DebugLogger(config);
}

export const debugLogger = createDebugLogger();

function resolveBrowserDebugLogConfig(dev: boolean): DebugLogConfig {
  const queryValue = readQueryDebugLogValue();
  if (queryValue !== null) {
    return resolveDebugLogConfig(queryValue, { dev, source: 'query' });
  }

  const storageValue = readStorageDebugLogValue();
  if (storageValue !== null) {
    return resolveDebugLogConfig(storageValue, { dev, source: 'storage' });
  }

  return resolveDebugLogConfig(null, { dev, source: 'default' });
}

function readQueryDebugLogValue(): string | null {
  try {
    const search = globalThis.location?.search ?? '';
    return new URLSearchParams(search).get(DEBUG_LOG_QUERY_PARAM);
  } catch {
    return null;
  }
}

function readStorageDebugLogValue(): string | null {
  try {
    return globalThis.localStorage?.getItem(DEBUG_LOG_STORAGE_KEY) ?? null;
  } catch {
    return null;
  }
}

function isDebugLogCategory(value: string): value is DebugLogCategory {
  return CATEGORY_SET.has(value);
}

function writeConsoleDebug(entry: DebugLogEntry): void {
  const prefix = `[KonaiDebug:${entry.category}] ${entry.message}`;
  if (entry.details === undefined) {
    console.debug(prefix);
    return;
  }

  console.debug(prefix, entry.details);
}
